/*
 * Справочники за стойкой: клиенты, каталог, сотрудники, график.
 *
 * Контрольная ширина — 1200–1366 (ноутбук за стойкой), но НЕ фиксированная: тот же экран
 * открывают на 1440 и на планшете. Поэтому здесь нет ни одной ширины в пикселях, кроме
 * тех, что назвал дизайнер (панель карточки услуги 400, правая колонка клиента 340,
 * поиск 360) — и все они держатся minmax, а не жёстким числом.
 *
 * Ни одного цвета мимо токенов: тёмная тема переворачивается сама.
 */

.screen {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

/* Шапка раздела — та же полоса 46 px, что у журнала: разделы не должны прыгать. */
.header {
  display: flex;
  flex: none;
  gap: var(--space-3);
  align-items: center;
  min-height: var(--topbar-height);
  padding: 0 var(--space-4);
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
}

.title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
}

.count {
  font-size: var(--font-size-md);
  color: var(--text-secondary);
}

.headerActions {
  display: flex;
  gap: var(--space-2);
  margin-left: auto;
}

/* Кнопки шапки не ломают подпись пополам: «+ Новый / клиент» читается как поломка. */
.headerActions > * {
  flex: none;
  white-space: nowrap;
}

/*
 * ТЕЛЕФОН. Если заголовок повторяет название раздела из шапки кабинета (вместе с датой
 * и филиалом), второй такой же под ней отнимал строку у списка: он остаётся только для
 * чтения голосом. Шапка раздела на телефоне — это счётчик и действия; если им тесно,
 * они переносятся целиком, а не по буквам. Шапка, в которой кроме повтора ничего нет
 * («Настройки»), на телефоне не рисуется вовсе — иначе на её месте пустая белая полоса.
 */
@media (max-width: 767px) {
  .header {
    flex-wrap: wrap;
    row-gap: var(--space-2);
    padding: var(--space-2) var(--space-4);
  }

  .header .titleRepeat,
  .headerBare {
    position: absolute;
    width: 1px;
    height: 1px;
    min-height: 0;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  .headerActions {
    flex-wrap: wrap;
  }
}

.toolbar {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-3) var(--space-4) var(--space-2);
}

.toolbarEnd {
  margin-left: auto;
  font-size: var(--font-size-md);
  color: var(--text-secondary);
  white-space: nowrap;
}

/* Поиск. Ширина из макета, но с minmax: на планшете он ужимается, а не выдавливает чипы. */
.search {
  position: relative;
  flex: 1 1 260px;
  max-width: 360px;
}

.search input {
  width: 100%;
  padding-left: 33px;
}

.searchIcon {
  position: absolute;
  top: 50%;
  left: 11px;
  width: 15px;
  height: 15px;
  color: var(--text-secondary);
  pointer-events: none;
  transform: translateY(-50%);
}

.body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  padding: 0 var(--space-4) var(--space-4);
}

.centerState {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
}

.centerCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: center;
  max-width: 520px;
  text-align: center;
}

.centerTitle {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
}

.centerText {
  margin: 0;
  font-size: var(--font-size-base);
  line-height: 1.5;
  color: var(--text-brand);
  text-wrap: pretty;
}

.centerNote {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.centerActions {
  display: flex;
  gap: var(--space-2);
}

/* Столбик действий пустого первого дня: три кнопки по 44 px, одна под другой. */
.stackedActions {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-2);
  width: 360px;
  max-width: 100%;
}

.stackedActions button {
  height: 44px;
}


/*
 * Поле ввода справочника.
 *
 * Повторяет .control из библиотеки, а не переиспользует его: CSS-модули не дают
 * сослаться на чужой класс, а тащить сюда TextField ради поиска без подписи значило бы
 * получить пустую строку метки над полем. Значения — те же токены, поэтому вид совпадает
 * и в тёмной теме тоже.
 */
.input {
  width: 100%;
  padding: 8px 11px;
  font-family: var(--font-ui);
  font-size: var(--font-size-base);
  color: var(--text-primary);
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  transition: border-color 0.15s, box-shadow 0.15s;
}

@media (pointer: coarse) {
  .input {
    min-height: var(--touch-min-target);
  }
}

.input::placeholder {
  color: var(--text-faint);
}

.input:focus {
  border-color: var(--accent);
  outline: none;
  box-shadow: var(--field-focus-ring);
}

.input:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: 4px;
}

.textarea {
  min-height: 64px;
  resize: vertical;
}

/* ─────────── таблица ─────────── */

.tableWrap {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: auto;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}

.table {
  width: 100%;
  font-size: var(--font-size-md);
  border-collapse: collapse;
}

.table th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 8px 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  text-align: left;
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
}

.table td {
  padding: 8px 10px;
  vertical-align: middle;
  border-bottom: 1px solid var(--grid-line-minor);
}

.table tbody tr:hover td {
  background: var(--bg-workspace);
}

/*
 * СПИСОК КЛИЕНТОВ НА ТЕЛЕФОНЕ (5-В5) — КАРТОЧКИ, А НЕ ТАБЛИЦА.
 *
 * Таблица из восьми столбцов в 360 px давала горизонтальную прокрутку (ТЗ её на 360
 * запрещает прямо), имя ломалось в три строки, а строка вырастала до 150 px, и на экран
 * помещался один клиент. Карточка: имя крупно сверху, телефон и последний визит под ним,
 * метки, внизу счётчики визитов и неявок и «Записать» справа — в зоне большого пальца.
 * Галочки массового выбора на телефоне нет: выгрузку выборки делают с компьютера.
 */
@media (max-width: 767px) {
  /*
   * На телефоне прокручивается СТРАНИЦА, а не рамка со списком. На ноутбуке рамка
   * со своей прокруткой держит шапку раздела и фильтры на месте; на телефоне та же
   * рамка давала прокрутку внутри прокрутки — палец листал то список, то страницу,
   * и до «Показать ещё» было не добраться.
   */
  .screen {
    height: auto;
  }

  .body,
  .tableWrap {
    flex: none;
    min-height: auto;
    overflow: visible;
  }

  .table thead {
    display: none;
  }

  .table,
  .table tbody {
    display: block;
  }

  .clientRow {
    display: grid;
    grid-template-columns: auto auto minmax(0, 1fr);
    grid-template-areas:
      "name name name"
      "phone phone visit"
      "tags tags tags"
      "visits noshow action";
    gap: 4px 12px;
    align-items: center;
    padding: 12px var(--space-4);
    border-bottom: 1px solid var(--border);
  }

  .clientRow > td {
    display: block;
    padding: 0;
    border: 0;
  }

  .table tbody .clientRow:hover > td {
    background: none;
  }

  .clientRow > td[data-cell="select"] {
    display: none;
  }

  .clientRow > td[data-cell="name"] {
    grid-area: name;
    font-size: var(--font-size-base);
  }

  .clientRow > td[data-cell="phone"] {
    grid-area: phone;
    color: var(--text-secondary);
  }

  .clientRow > td[data-cell="visit"] {
    grid-area: visit;
    color: var(--text-secondary);
    text-align: right;
  }

  .clientRow > td[data-cell="tags"] {
    grid-area: tags;
  }

  .clientRow > td[data-cell="tags"]:empty,
  .clientRow > td[data-cell="tags"]:has(> div:empty) {
    display: none;
  }

  /* Визиты и неявки — в одну строку с подписями: числа без подписей на карточке немые. */
  .clientRow > td[data-cell="visits"] {
    grid-area: visits;
    text-align: left;
    color: var(--text-secondary);
  }

  .clientRow > td[data-cell="visits"]::before {
    content: "Визиты: ";
  }

  /* Цвет не задаётся: у ненулевых неявок свой тревожный цвет (.noShow), и карточка
     обязана его сохранить — это первое, что стойка ищет глазами. */
  .clientRow > td[data-cell="noshow"] {
    grid-area: noshow;
    text-align: left;
  }

  .clientRow > td[data-cell="noshow"]::before {
    content: "Неявки: ";
  }

  .clientRow > td[data-cell="action"] {
    grid-area: action;
    justify-self: end;
  }
}

.num {
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.nowrap {
  white-space: nowrap;
}

.rowMain {
  display: block;
  font-weight: var(--font-weight-semibold);
}

.rowSub {
  display: block;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.rowAlert {
  display: block;
  font-size: var(--font-size-sm);
  color: var(--status-no-show-fg);
}

.noShow {
  font-weight: var(--font-weight-semibold);
  color: var(--status-no-show-fg);
}

.morePhones {
  color: var(--text-faint);
}

.tableFoot {
  display: flex;
  flex: none;
  gap: var(--space-3);
  justify-content: space-between;
  padding: 10px 12px;
  font-size: var(--font-size-md);
  color: var(--text-secondary);
  background: var(--bg-card);
  border-top: 1px solid var(--grid-line-minor);
}

.tagRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

/* ─────────── врезка-пояснение ─────────── */

.note {
  padding: 9px 12px;
  font-size: var(--font-size-md);
  line-height: 1.45;
  color: var(--text-brand);
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-left: var(--note-accent-width) solid var(--status-pending-fg);
  border-radius: var(--radius-md);
}

.noteStrong {
  font-weight: var(--font-weight-semibold);
}

.hintBox {
  padding: 9px 12px;
  font-size: var(--font-size-md);
  line-height: 1.5;
  color: var(--info-fg);
  background: var(--info-bg);
  border-radius: var(--radius-md);
}

.warnBox {
  padding: 8px 12px;
  font-size: var(--font-size-md);
  line-height: 1.45;
  color: var(--status-pending-fg);
  background: var(--status-pending-bg);
  border-radius: var(--radius-md);
}

.dangerBox {
  padding: 8px 12px;
  font-size: var(--font-size-md);
  line-height: 1.45;
  color: var(--status-no-show-fg);
  background: var(--status-no-show-bg);
  border-radius: var(--radius-md);
}

/* ─────────── две колонки: список и карточка ─────────── */

.split {
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 400px);
  min-height: 0;
}

.splitHalves {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.splitLeft {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: auto;
  border-right: 1px solid var(--border);
}

.splitRight {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  padding: var(--space-3) var(--space-4);
  overflow: auto;
  background: var(--bg-card);
}

/*
 * Ниже 1100 колонки встают одна под другую: карточка услуги на 320 px рядом со списком
 * превращает и то и другое в колодец. Это не «мобильная версия» — это ноутбук 13″.
 */
@media (width <= 1100px) {
  .split {
    display: flex;
    flex-direction: column;
    overflow: auto;
  }

  .splitLeft {
    border-right: 0;
    border-bottom: 1px solid var(--border);
  }
}

/*
 * На телефоне прокручивается страница, а не колонки: у списка и карточки своих прокруток
 * нет (та же причина, что у списка клиентов, — прокрутка внутри прокрутки).
 */
@media (max-width: 767px) {
  .split,
  .splitLeft,
  .splitRight {
    flex: none;
    overflow: visible;
  }
}

.sectionLabel {
  font-size: var(--font-size-xs-plus);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* ─────────── строки-карточки (каталог, сотрудники) ─────────── */

.cardList {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
}

.groupCard {
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}

.groupHead {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  padding: 9px 14px;
  background: var(--bg-card);
  border-bottom: 1px solid var(--grid-line-minor);
}

.groupName {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
}

.groupCount {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.rowItem {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  width: 100%;
  padding: 8px 14px;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  background: none;
  border: 0;
  border-bottom: 1px solid var(--grid-line-minor);
}

.rowItem:last-child {
  border-bottom: 0;
}

.rowItem:hover {
  background: var(--bg-workspace);
}

.rowItemActive {
  background: var(--bg-workspace);
  box-shadow: inset 3px 0 0 var(--accent);
}

.rowGrow {
  flex: 1;
  min-width: 0;
}

.rowTitle {
  overflow: hidden;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Имя мастера кнопкой — вход в публичный профиль (3-Б7): выглядит как текст строки. */
.rowTitleLink {
  max-width: 100%;
  padding: 0;
  font-family: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  background: none;
  border: 0;
}

.rowTitleLink:hover {
  text-decoration: underline;
}

.rowMeta {
  flex: none;
  font-size: var(--font-size-md);
  color: var(--text-secondary);
  white-space: nowrap;
}

.rowMetaAlert {
  flex: none;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--status-pending-fg);
  white-space: nowrap;
}

.rowPrice {
  flex: none;
  width: 90px;
  font-variant-numeric: tabular-nums;
  font-weight: var(--font-weight-semibold);
  text-align: right;
}

/* Цветная полоса мастера. Цвет приходит из данных — он и есть колонка в журнале. */
.masterStripe {
  flex: none;
  width: 10px;
  height: 28px;
  border: 1px solid var(--master-avatar-ring);
  border-radius: 3px;
}

/*
 * СТРОКА УСЛУГИ НА ТЕЛЕФОНЕ. На ноутбуке это строка прайса: название, длительность,
 * мастера, цена и «онлайн» в одну линию. В 360 px на название с длительностью оставалось
 * 57 px, и «45 мин + буферы 0/10» ломалось в три строки. На телефоне строка — как в меню
 * прайса: название и цена сверху, длительность, мастера и «онлайн» под ними.
 */
.serviceOnline {
  display: flex;
  flex: none;
}

@media (max-width: 767px) {
  .serviceRow {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    grid-template-areas:
      "title title price"
      "sub meta online";
    gap: 4px var(--space-2);
    align-items: center;
    padding: 10px 14px;
  }

  .serviceRow > .rowGrow {
    display: contents;
  }

  .serviceTitle {
    grid-area: title;
    white-space: normal;
  }

  .serviceSub {
    grid-area: sub;
    min-width: 0;
  }

  .serviceMeta {
    grid-area: meta;
  }

  .servicePrice {
    grid-area: price;
    width: auto;
  }

  .serviceOnline {
    grid-area: online;
    justify-content: flex-end;
  }
}

/* Значки строки сотрудника («исполнитель · в тарифе», «на витрине») — одной группой. */
.rowBadges {
  display: flex;
  flex: none;
  gap: var(--space-3);
  align-items: center;
}

/*
 * СТРОКА СОТРУДНИКА НА ТЕЛЕФОНЕ. На ноутбуке полоса, имя, значки и меню стоят в одну
 * строку; в 360 px они делили ширину поровну, и подпись «Без должности · 2 услуги»
 * ломалась по слову в столбик, а значки — пополам. На телефоне первая строка — полоса,
 * имя и меню «⋯», а значки уходят под имя целой строкой, с отступом ровно по имени.
 */
@media (max-width: 767px) {
  .rowStatic {
    flex-wrap: wrap;
    row-gap: 6px;
  }

  .rowStatic > .rowBadges {
    flex-wrap: wrap;
    flex-basis: 100%;
    order: 1;
    gap: 6px;
    padding-left: calc(10px + var(--space-3));
  }
}

/* ─────────── карточка клиента 3-А2 ─────────── */

.crumbs {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  min-height: var(--topbar-height);
  padding: 0 var(--space-4);
  font-size: var(--font-size-md);
  color: var(--text-secondary);
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
}

.crumbCurrent {
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.crumbLink {
  padding: 0;
  font: inherit;
  color: var(--text-secondary);
  text-decoration: underline;
  cursor: pointer;
  background: none;
  border: 0;
}

.cardGrid {
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 340px);
  gap: var(--space-4);
  align-content: start;
  min-height: 0;
  padding: var(--space-4);
  overflow: auto;
}

@media (width <= 1100px) {
  .cardGrid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.cardColumn {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}

.clientHead {
  display: flex;
  gap: var(--space-4);
  align-items: flex-start;
}

.avatar {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  border-radius: 50%;
}

.clientName {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
}

.clientSub {
  font-size: var(--font-size-md);
  color: var(--text-secondary);
}

.pinnedNote {
  display: flex;
  gap: var(--space-2);
  padding: 8px 12px;
  font-size: var(--font-size-md);
  line-height: 1.45;
  color: var(--status-pending-fg);
  background: var(--status-pending-bg);
  border-radius: var(--radius-md);
}

/*
 * Пять счётчиков в ряд. Ниже 1100 — по три: пять колонок по 60 px превращают
 * «31 400 ₽» в «31…», и число, ради которого их смотрят, исчезает.
 */
.stats {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: var(--space-2);
  font-variant-numeric: tabular-nums;
}

@media (width <= 1100px) {
  .stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.stat {
  padding: 8px 10px;
  background: var(--bg-workspace);
  border-radius: var(--radius-md);
}

.statKey {
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.statValue {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
}

.statValueAlert {
  color: var(--status-no-show-fg);
}

.statValueEmpty {
  color: var(--text-secondary);
}

.tabs {
  display: flex;
  gap: var(--space-1);
  padding: 0 var(--space-3);
  border-bottom: 1px solid var(--border);
}

.tab {
  display: flex;
  gap: 6px;
  align-items: center;
  min-height: 38px;
  padding: 0 10px;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  cursor: pointer;
  background: none;
  border: 0;
  border-bottom: 2px solid transparent;
}

.tabActive {
  color: var(--text-primary);
  border-bottom-color: var(--accent);
}

.tabCount {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.visitRow {
  display: flex;
  gap: var(--space-4);
  align-items: center;
  padding: 10px 18px;
  border-bottom: 1px solid var(--grid-line-minor);
}

.visitWhen {
  flex: none;
  width: 110px;
  font-variant-numeric: tabular-nums;
}

.sideRow {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-md);
}

.sideValue {
  font-variant-numeric: tabular-nums;
  font-weight: var(--font-weight-semibold);
}

.sideOk {
  font-weight: var(--font-weight-semibold);
  color: var(--status-in-progress-fg);
}

.sideMuted {
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}

.divider {
  height: 1px;
  background: var(--border);
}

.inlineLink {
  padding: 0;
  font: inherit;
  font-weight: var(--font-weight-semibold);
  color: var(--text-brand);
  text-decoration: underline;
  cursor: pointer;
  background: none;
  border: 0;
}

/* ─────────── график 3-Б8 ─────────── */

.scheduleHead {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.scheduleGrid {
  display: grid;
  grid-template-columns: 120px repeat(7, minmax(0, 1fr));
  gap: 3px;
  font-size: var(--font-size-2xs);
  font-variant-numeric: tabular-nums;
}

.scheduleDay {
  font-weight: var(--font-weight-medium);
  line-height: 1.2;
  color: var(--text-secondary);
  text-align: center;
}

.scheduleToday {
  font-weight: 700;
  color: var(--now);
}

.scheduleSunday {
  color: var(--text-faint);
}

.scheduleName {
  display: flex;
  gap: 6px;
  align-items: center;
  overflow: hidden;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scheduleNameDot {
  flex: none;
  width: 6px;
  height: 14px;
  border-radius: 2px;
}

/*
 * ГРАФИК НА ТЕЛЕФОНЕ. Столбец имён в 120 px оставлял дням по 25 px, и в клетке
 * обрезалось всё: «отпуск» до «отпу», «правка» до «равк». На телефоне имя мастера
 * встаёт строкой над его неделей, а семь дней делят всю ширину — по 42 px на день,
 * и «10–20», «правка», «празд.» помещаются целиком. Что длиннее клетки («отсутствие»),
 * обрезается многоточием, а не посреди буквы; полный текст — в подсказке и голосом.
 */
@media (max-width: 767px) {
  .scheduleGrid {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  .scheduleCorner {
    display: none;
  }

  .scheduleName {
    grid-column: 1 / -1;
    padding-top: var(--space-2);
  }

  .cell > span[aria-hidden="true"] {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/*
 * Клетка дня. Четыре вида различаются НЕ ТОЛЬКО цветом: у смены надпись с часами,
 * у правки — пунктирная рамка и приписка, у отсутствия — слово, у праздника — знак
 * вопроса. В градациях серого и для дальтоника это по-прежнему четыре разных дня.
 */
.cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: center;
  justify-content: center;
  height: 34px;
  overflow: hidden;
  font-size: var(--font-size-2xs);
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
  border: 1px solid transparent;
  border-radius: var(--radius-xs);
}

.cellShift {
  color: var(--status-in-progress-fg);
  background: var(--status-in-progress-bg);
}

.cellOverride {
  color: var(--status-pending-fg);
  background: var(--bg-card);
  border: 1px dashed var(--status-pending-fg);
}

.cellTimeoff {
  color: var(--status-no-show-fg);
  background: var(--status-no-show-bg);
}

.cellHoliday {
  color: var(--status-pending-fg);
  background: var(--status-pending-bg);
}

.cellNone {
  color: var(--text-faint);
  background: var(--bg-unassigned);
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  font-size: var(--font-size-xs-plus);
  color: var(--text-secondary);
}

.legendSwatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 4px;
  vertical-align: -1px;
  border-radius: 2px;
}

/* ─────────── «мастер выбывает» 2-2.10.4 ─────────── */

.steps {
  display: flex;
  gap: 6px;
}

.step {
  flex: 1;
  padding: 6px 8px;
  font-size: var(--font-size-xs-plus);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  text-align: center;
  background: var(--bg-sunken);
  border-radius: 6px;
}

.stepActive {
  color: var(--text-on-nav-active);
  background: var(--bg-nav);
}

.option {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  padding: 9px 12px;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
}

.optionActive {
  border: 1.5px solid var(--accent);
}

.optionHead {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;
}

.previewTiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  font-size: var(--font-size-sm);
}

.tileOk {
  padding: 6px 8px;
  color: var(--status-in-progress-fg);
  background: var(--status-in-progress-bg);
  border-radius: 6px;
}

.tileWarn {
  padding: 6px 8px;
  color: var(--status-pending-fg);
  background: var(--status-pending-bg);
  border-radius: 6px;
}

.tileDanger {
  padding: 6px 8px;
  color: var(--status-no-show-fg);
  background: var(--status-no-show-bg);
  border-radius: 6px;
}

.summaryBox {
  padding: 10px 12px;
  font-size: var(--font-size-md);
  line-height: 1.5;
  background: var(--bg-workspace);
  border-radius: var(--radius-md);
}

.dialogActions {
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
}

/* Форма, живущая на своём адресе: не окно, а экран. Ширина 620 — из макета. */
.sheet {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: 100%;
  max-width: 620px;
  padding: 18px 20px;
  margin: var(--space-4) auto;
  font-size: var(--font-size-md);
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
}

.sheetScroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fieldLabel {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
}

.skeletonCell {
  height: 11px;
  border-radius: 3px;
}

/* ─────────── мелочи, которым не место в inline style ─────────── */

.blockPad {
  padding: var(--space-4) 18px;
}

.emptyTab {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  align-items: flex-start;
  padding: 22px 18px;
}

.tagRowTop {
  margin-top: 6px;
}

.checkCol {
  width: 36px;
}

.tabularNums {
  font-variant-numeric: tabular-nums;
}

/* Строка-карточка, по которой не кликают (сотрудник): курсор и подсветка неуместны —
   они обещают переход, которого нет. */
.rowStatic {
  cursor: default;
}

.rowStatic:hover {
  background: none;
}
